import * as React from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Alert from '@mui/material/Alert';
import Select from '@mui/material/Select';
import Divider from '@mui/material/Divider';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker';
import { TimeValidationError } from '@mui/x-date-pickers/models';
import { toast } from 'react-toastify';
import * as Device from 'react-device-detect';

import {
  BookingOccasionEnum,
  BookingStatusEnum,
  useUpdateBookingMutation,
  GetConsumerBookingQuery,
} from '@graphql';
import { useSession } from '@hooks';
import { shouldDisableBookingDate, shouldDisableBookingTime, getDateSchedules } from '@utils';
import validationSchema from './validationSchema';
import ConfirmCancel from './ConfirmCancel';
import ConfirmEdit from './ConfirmEdit';

interface EditConsumerFormProps {
  handleEdit: () => void;
  booking: GetConsumerBookingQuery['booking'];
}

export default function EditConsumerFormForm(props: Readonly<EditConsumerFormProps>) {
  const { booking, handleEdit } = props;
  const [dateSchedules, setDateSchedules] = React.useState<string | null>(null);
  const [isDateClosed, setIsDateClosed] = React.useState(false);
  const [isDatePassed, setIsDatePassed] = React.useState(false);
  const [timeError, setTimeError] = React.useState<TimeValidationError | null>(null);
  const [isDisabled, setIsDisabled] = React.useState(true);
  const { account } = useSession();
  const { t } = useTranslation('booking');

  const size = Device.isMobileOnly ? 'small' : 'medium';

  const [updateBooking] = useUpdateBookingMutation();

  const defaultValues = {
    startTime: moment(booking.startTime).tz('Europe/Paris'),
    participants: booking.participants.toString(),
    occasion: booking.occasion ?? '',
    status: booking.status,
  };

  const formik = useFormik({
    initialValues: defaultValues,
    validationSchema: validationSchema(booking, t, account?.__typename),
    onSubmit: async (values) => {
      // only update the values that are different from the default ones
      const validValues = {
        ...(!defaultValues.startTime.isSame(values.startTime) && {
          startTime: values.startTime.toISOString(),
        }),
        ...(values.participants !== defaultValues.participants && { participants: +values.participants }),
        ...(values.occasion !== defaultValues.occasion && {
          occasion: values.occasion as BookingOccasionEnum,
        }),
        ...(values.status !== defaultValues.status && { status: values.status }),
      };

      updateBooking({
        refetchQueries: ['GetConsumerBooking'],
        awaitRefetchQueries: true,
        variables: { input: { id: booking.id, ...validValues } },
        onCompleted() {
          handleEdit();
          toast.success(t('update.success'));
        },
        onError() {
          toast.error(t('update.error'));
        },
      });
    },
  });

  const handleCancelBooking = async () => {
    await formik.setFieldValue('status', BookingStatusEnum.Canceled);
    formik.handleSubmit();
  };

  const handleConfirmEdit = async () => {
    formik.handleSubmit();
  };

  React.useEffect(() => {
    const dateSchedules = getDateSchedules(
      booking.spot.opening,
      booking.spot.closing,
      moment(formik.values.startTime).startOf('day')
    );

    setDateSchedules(dateSchedules);

    const isClosed = booking.spot
      ? shouldDisableBookingDate(moment(formik.values.startTime), booking.spot.opening, booking.spot.closing)
      : true;

    setIsDateClosed(isClosed);
    setIsDatePassed(formik.values.startTime.clone().endOf('day').isBefore());
  }, [formik.values.startTime]);

  React.useEffect(() => {
    if (isDateClosed) {
      setTimeError(null);
      formik.resetForm();
    } else if (isDatePassed) {
      formik.setFieldError('startTime', undefined);
    } else if (!timeError) {
      formik.setFieldError('startTime', undefined);
    } else if (timeError === 'shouldDisableTime-hours') {
      formik.setFieldError('startTime', 'Le spot est fermé à cette heure là');
    } else if (timeError === 'disablePast') {
      formik.setFieldError('startTime', 'Cette heure pour cette date est dépassée');
    }
  }, [isDateClosed, timeError, isDatePassed]);

  React.useEffect(() => {
    // handle modifications
    const isDisabled = isDateClosed || isDatePassed || !formik.dirty || !formik.isValid;
    setIsDisabled(isDisabled);
  }, [formik, isDatePassed, isDateClosed]);

  return (
    <Stack
      height="100%"
      component="form"
      spacing={5}
      justifyContent="space-between"
      onSubmit={formik.handleSubmit}
    >
      <Stack spacing={2} width="100%" divider={<Divider />}>
        <FormControl fullWidth error={isDateClosed}>
          <DateCalendar
            disablePast
            timezone="Europe/Paris"
            value={formik.values.startTime}
            onChange={(value) => {
              if (value) {
                setTimeError(null);
                formik.setFieldValue('startTime', value);
              }
            }}
            shouldDisableDate={(date) => {
              return shouldDisableBookingDate(date, booking.spot.opening, booking.spot.closing);
            }}
            sx={{ width: '100%' }}
          />
          <Stack spacing={1}>
            {!isDateClosed && !isDatePassed && (
              <Alert severity="info" icon={false} sx={{ width: '100%' }}>
                <Typography variant="body2" fontWeight={500}>
                  {t('alert.Schedule for the selected day', {
                    ns: 'booking',
                    dateSchedules,
                  })}
                </Typography>
              </Alert>
            )}
            {isDateClosed && (
              <Alert severity="warning" icon={false}>
                {t('alert.spot is closed this day', { ns: 'booking', spot: booking.spot.name })}
              </Alert>
            )}
            {isDatePassed && (
              <Alert severity="warning" icon={false}>
                {t('alert.The date of dateTime has passed', {
                  ns: 'booking',
                  dateTime: formik.values.startTime.format('LL'),
                })}
              </Alert>
            )}
          </Stack>
        </FormControl>
        <DesktopTimePicker
          label="Heure d'arrivée"
          disablePast
          closeOnSelect
          timezone="Europe/Paris"
          value={formik.values.startTime}
          ampm={false}
          onError={(newError) => setTimeError(newError)}
          disabled={isDateClosed || isDatePassed}
          timeSteps={{ hours: 1, minutes: 15, seconds: 60 }}
          slotProps={{
            textField: {
              size: size,
              helperText: formik.errors.startTime as string | undefined,
              error: !!formik.errors.startTime,
            },
          }}
          onChange={(value) => {
            if (value) {
              setTimeError(null);
              formik.setFieldValue('startTime', value);
            }
          }}
          shouldDisableTime={(date) => {
            return shouldDisableBookingTime(date, booking.spot.opening, booking.spot.closing);
          }}
        />
        <TextField
          {...formik.getFieldProps('participants')}
          required
          fullWidth
          size={size}
          variant="outlined"
          slotProps={{ input: { min: 1 } }}
          type="number"
          id="book-participants"
          name="participants"
          label={t('participants.label', { ns: 'field' })}
          onChange={(e) => {
            const value = Math.max(1, parseInt(e.target.value, 10));
            formik.setFieldValue('participants', isNaN(value) ? '' : value);
          }}
          disabled={isDateClosed || formik.values.startTime.isBefore()}
          error={formik.touched.participants && Boolean(formik.errors.participants)}
          helperText={
            formik.touched.participants && formik.errors.participants
              ? String(formik.errors.participants)
              : null
          }
        />
        <FormControl fullWidth size={size} hiddenLabel>
          <InputLabel
            id="occasion-select-label"
            shrink
            disabled={isDateClosed || formik.values.startTime.isBefore()}
          >
            {t('occasion select.label', { ns: 'field' })}
          </InputLabel>
          <Select
            {...formik.getFieldProps('occasion')}
            labelId="occasion-select-label"
            name="occasion"
            label={t('occasion select.label', { ns: 'field' })}
            autoComplete="off"
            displayEmpty
            disabled={isDateClosed || formik.values.startTime.isBefore()}
          >
            <MenuItem autoFocus value="">
              <em>{t('occasion select.placeholder', { ns: 'field' })}</em>
            </MenuItem>
            {Object.entries(BookingOccasionEnum).map(([key, value]) => (
              <MenuItem key={key} value={value}>
                {t(`suitable.${key.charAt(0).toLowerCase() + key.slice(1)}`, {
                  ns: 'spot',
                })}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText id="occasion-helper-text">{t('word.Optional', { ns: 'common' })}</FormHelperText>
        </FormControl>
      </Stack>
      <Stack
        spacing={Device.isMobileOnly ? 2 : 3}
        direction={Device.isMobileOnly ? 'column-reverse' : 'row'}
        pb={`calc(env(safe-area-inset-bottom) + 24px)`}
      >
        <ConfirmCancel handleConfirm={handleCancelBooking} />
        <ConfirmEdit
          handleConfirm={handleConfirmEdit}
          disabled={isDisabled}
          startTime={formik.values.startTime}
          participants={+formik.values.participants}
          occasion={(formik.values.occasion as BookingOccasionEnum) ?? null}
        />
      </Stack>
    </Stack>
  );
}
